import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import moment from "moment";
import { AppService } from "src/app/core/services/app.service";
import { FocuslinkService } from "src/app/core/services/focuslink.service";
import { LocalDataStoreService } from "src/app/core/services/local-data-store.service";
import { OOVenueService } from "src/app/core/services/venue-service";
import { AppleWalletPreferences } from "src/app/shared/models/AppleWalletPreferences.model";
import { EGiftVenue } from "src/app/shared/models/eGift/EGiftVenue.model";
import { AppleWalletType } from "src/app/shared/models/enums/apple-wallet-type.enum";
import { AppMessageSeverity } from "src/app/shared/models/enums/AppMessageSeverity.enum";
import { GiftCardAppleWalletSetupComponent } from "./gift-card-apple-wallet-setup/gift-card-apple-wallet-setup.component";

@Component({
	selector: "app-gift-card-setup",
	templateUrl: "./gift-card-setup.component.html",
	styleUrls: ["../../commandcenter.scss"],
})
export class GiftCardSetupComponent implements OnInit, OnDestroy {
	@ViewChild("giftCardAppleWalletSetupComponent") giftCardAppleWalletSetupComponent: GiftCardAppleWalletSetupComponent;

	changesToSave: boolean = false;

	denominationAdd: boolean = false;
	denominationAmountNew: number;
	displayGiftSetup: boolean = false;

	giftProcessors = [
		{ value: 0, label: "None" },
		{ value: 1, label: "MyFocus Gift" },
		{ value: 2, label: "Focus CA Gift" },
	];

	eGiftVenue: EGiftVenue;

	loaded: boolean = false;
	logoSrc: string;

	transientGift = {
		processor: undefined,
		apiKey: undefined,
		apiSecret: undefined,
	};
	timestamp: number;

	viewMode: number = 0;

	walletPreferences: AppleWalletPreferences;

	constructor(
		public app: AppService,
		private focuslink: FocuslinkService,
		private localStorage: LocalDataStoreService,
		public venueService: OOVenueService
	) {}
	ngOnDestroy(): void {
		this.onSave();
	}

	ngOnInit(): void {
		this.getEgiftVenue(this.venueService.venueKey);
	}

	async getEgiftVenue(venueKey) {
		try {
			const resp = await this.focuslink.eGiftVenueGet(venueKey);
			this.eGiftVenue = new EGiftVenue(resp);

			if (!this.eGiftVenue.Venue.ooVenue?.giftProcessor) {
				this.displayGiftSetup = true;
			}

			if (!this.eGiftVenue?.logoS3Key) {
				this.logoSrc = `https://focuspos-onlineorder-images.s3-us-west-2.amazonaws.com/${this.venueService.venueKey}/logo.png`;
			} else {
				this.logoSrc = `https://focuspos-onlineorder-images.s3-us-west-2.amazonaws.com/${this.venueService.venueKey}/${this.eGiftVenue.logoS3Key}`;
			}
			this.walletPrefGet(venueKey);
		} catch (error) {
			console.log(error);
		}
	}

	async ooVenueSave() {
		this.venueService.ooVenueSave(this.venueService.ooVenue);
	}

	async onSave() {
		await this.focuslink.eGiftVenuePost(this.venueService.venueKey, this.eGiftVenue);
		if (this.walletPreferences) {
			this.saveWalletPreferences();
		}

		this.app.msgSvc.add({ severity: AppMessageSeverity.GREEN, summary: "", detail: `Gift card settings saved successfully` });
	}

	async saveGiftSettings() {
		this.venueService.ooVenue.giftProcessor = this.transientGift.processor;
		this.venueService.ooVenue.giftApiKey = this.transientGift.apiKey;
		this.venueService.ooVenue.giftApiSecret = this.transientGift.apiSecret;
		await this.ooVenueSave();
		this.displayGiftSetup = false;
		this.getEgiftVenue(this.venueService.venueKey);
	}

	async saveWalletPreferences() {
		this.walletPreferences.labelColor = this.walletPreferences.foregroundColor;
		this.walletPreferences.foregroundColor = AppleWalletPreferences.fromPrimeNgColor(this.walletPreferences.foreground);
		this.walletPreferences.backgroundColor = AppleWalletPreferences.fromPrimeNgColor(this.walletPreferences.background);
		this.focuslink.appleWalletPreferencePost(this.venueService.subscriptionVenue.value.key, this.walletPreferences);
		console.log("Wallet saved");
	}

	updateTimestamp() {
		this.timestamp = moment().unix();
	}

	async walletPrefGet(venueKey: number) {
		try {
			const resp = await this.focuslink.appleWalletPreferenceGet(venueKey);
			const walletPrefs: AppleWalletPreferences[] = resp.map((i) => {
				return new AppleWalletPreferences(i);
			});
			if (walletPrefs?.length > 0) {
				this.walletPreferences = walletPrefs.find((i) => i.walletType === AppleWalletType.GIFT);
				if (!this.walletPreferences) {
					this.walletPreferences = this.createNewWalletPass();
					this.saveWalletPreferences();
				}
			} else {
				this.walletPreferences = this.createNewWalletPass();
				this.saveWalletPreferences();
			}

			if (this.walletPreferences.backgroundColor)
				this.walletPreferences.background = AppleWalletPreferences.parseColor(this.walletPreferences.backgroundColor);
			if (this.walletPreferences.foregroundColor)
				this.walletPreferences.foreground = AppleWalletPreferences.parseColor(this.walletPreferences.foregroundColor);
		} catch (error) {
			console.log(error);
			this.walletPreferences = new AppleWalletPreferences({ venueKey: this.venueService.venueKey });
		}
	}

	createNewWalletPass(): AppleWalletPreferences {
		return new AppleWalletPreferences({
			venueKey: this.venueService.subscriptionVenue.value.key,
			organizationName: this.venueService.subscriptionVenue.value.name,
			secondaryText: "Gift card (:cardNumber)",
			logoText: this.venueService.subscriptionVenue.value.name,
			descriptionText: "Gift card",
			balanceLabel: "Balance",
			foregroundColor: "rgb(0,0,0)",
			backgroundColor: "rgb(255,255,255)",
			labelColor: "rgb(0,0,0)",
			walletType: AppleWalletType.GIFT,
			background: AppleWalletPreferences.parseColor("rgb(255,255,255)"),
			foreground: AppleWalletPreferences.parseColor("rgb(0,0,0)"),
		});
	}

	async walletPrefPost(venueKey: number) {
		const resp = await this.focuslink.appleWalletPreferencePost(venueKey, this.walletPreferences);
	}
}
